import React, { useState } from 'react';

/**
 * Laws and Regulations Guidance Component
 * Provides specialized guidance for tasks related to laws and regulations mapping
 */
function LawsRegulationsGuidance({ task, fileInputRef, uploading, files, onFileUpload }) {
  const [showUpload, setShowUpload] = useState(false);
  const hasFiles = files && files.length > 0;

  const relevantLaws = [
    { name: 'Arbeidsmiljøloven', url: 'https://lovdata.no/dokument/NL/lov/2005-06-17-62', description: 'Alle virksomheter med ansatte' },
    { name: 'Internkontrollforskriften', url: 'https://lovdata.no/dokument/SF/forskrift/1996-12-06-1127', description: 'Systematisk HMS-arbeid' },
    { name: 'Brann- og eksplosjonsvernloven', url: 'https://lovdata.no/dokument/NL/lov/2002-06-14-20', description: 'Brannvern og rømning' },
    { name: 'Forskrift om organisering, ledelse og medvirkning', url: 'https://lovdata.no/dokument/SF/forskrift/2011-12-06-1355', description: 'Verneombud, AMU' },
    { name: 'Forurensningsloven', url: 'https://lovdata.no/dokument/NL/lov/1981-03-13-6', description: 'Miljøpåvirkning (hvis relevant)' },
  ];

  const industrySpecific = [
    { industry: 'Bygg og anlegg', law: 'Byggherreforskriften' },
    { industry: 'Helse og omsorg', law: 'Helsepersonelloven, Pasientrettighetsloven' },
    { industry: 'Næringsmidler', law: 'Matloven, Næringsmiddelhygieneforskriften' },
    { industry: 'Transport', law: 'Yrkestransportloven, Arbeidstidsforskriften' },
    { industry: 'Kjemikalier', law: 'REACH-forskriften, Stoffkartotek-forskriften' },
  ];

  return (
    <div data-testid="laws-regulations-guidance">
      {/* Why this is important */}
      <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-900 mb-1">Fundamentet for lovlig drift</p>
            <p className="text-sm text-amber-800">
              Oversikt over gjeldende lover beskytter virksomheten mot bøter og pålegg, 
              og sikrer at dere oppfyller minimumskravene for trygg drift.
            </p>
          </div>
        </div>
      </div>

      {/* Quick action */}
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
        Slik går du frem
      </p>

      {/* Relevant laws for all businesses */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Lover som gjelder de fleste virksomheter
        </h4>
        <div className="space-y-2">
          {relevantLaws.map((law, idx) => (
            <a
              key={idx}
              href={law.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-2 bg-white rounded-lg border border-blue-100 hover:border-blue-300 transition-colors"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">{law.name}</p>
                <p className="text-xs text-gray-500">{law.description}</p>
              </div>
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          ))}
        </div>
      </div>

      {/* Industry specific */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Bransjespesifikke forskrifter
        </h4>
        <p className="text-sm text-gray-600 mb-3">Sjekk om noen av disse gjelder din virksomhet:</p>
        <div className="grid grid-cols-2 gap-2">
          {industrySpecific.map((item, idx) => (
            <div key={idx} className="p-2 bg-white rounded-lg border border-gray-100">
              <p className="text-xs font-medium text-gray-700">{item.industry}</p>
              <p className="text-xs text-gray-500">{item.law}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Resource link */}
      <a
        href="https://www.arbeidstilsynet.no/regelverk/"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl mb-4 hover:bg-green-100 transition-colors"
      >
        <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="font-semibold text-green-900">Arbeidstilsynets regelverksoversikt</p>
          <p className="text-sm text-green-700">Komplett oversikt sortert etter bransje</p>
        </div>
        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>

      {/* Upload option */}
      <div className="border border-gray-200 rounded-xl p-4">
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="w-full flex items-center gap-3 text-left"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <span className="font-medium text-gray-900">Dokumenter din lovoversikt</span>
            <p className="text-sm text-gray-500 mt-0.5">
              Last opp en liste over relevante lover for din virksomhet
            </p>
          </div>
          <svg className={`w-5 h-5 text-gray-400 transition-transform ${showUpload ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showUpload && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Laster opp...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Last opp dokument
                </>
              )}
            </button>
            {hasFiles && (
              <p className="text-xs text-green-600 mt-2 text-center">
                ✓ {files.length} dokument(er) lastet opp
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LawsRegulationsGuidance;
